import React from 'react';
import { X, Trophy, Flag, Gauge, Calendar, Wrench, Star, Check, ArrowLeft } from 'lucide-react';

export default function EntityDetailModal({ entity, type, isOpen, onClose, onSelect, isSelected }) {
  if (!isOpen || !entity) return null;

  const getTypeConfig = () => {
    switch (type) {
      case 'driver':
        return {
          title: 'Driver Profile',
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary',
          icon: Flag
        };
      case 'rider':
        return {
          title: 'Rider Profile',
          color: 'text-secondary',
          bgColor: 'bg-secondary/10',
          borderColor: 'border-secondary',
          icon: Flag
        };
      case 'car':
        return {
          title: 'Car Details',
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary',
          icon: Gauge
        };
      case 'bike':
        return {
          title: 'Bike Details',
          color: 'text-secondary',
          bgColor: 'bg-secondary/10',
          borderColor: 'border-secondary',
          icon: Gauge
        };
      case 'tuner':
      case 'moto_tuner':
        return {
          title: 'Team Profile',
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-500/10',
          borderColor: 'border-yellow-500',
          icon: Wrench
        };
      default:
        return {
          title: 'Details',
          color: 'text-white',
          bgColor: 'bg-zinc-800',
          borderColor: 'border-zinc-700',
          icon: Star
        };
    }
  };

  const config = getTypeConfig();
  const Icon = config.icon;

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className={`bg-card border-2 ${config.borderColor} max-w-lg w-full max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`${config.bgColor} p-6 relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
          
          <div className="flex items-start space-x-4">
            {entity.image_url ? (
              <img 
                src={entity.image_url} 
                alt={entity.name}
                className="w-24 h-24 object-cover border-2 border-zinc-700"
              />
            ) : (
              <div className={`w-24 h-24 ${config.bgColor} border-2 ${config.borderColor} flex items-center justify-center`}>
                <Icon size={40} className={config.color} />
              </div>
            )}
            
            <div className="flex-1">
              <p className={`text-sm ${config.color} font-bold uppercase tracking-wider`}>
                {config.title}
              </p>
              <h2 className="font-heading font-black text-2xl text-white mt-1">
                {entity.name}
              </h2>
              {(type === 'car' || type === 'bike') && entity.number && (
                <p className="text-zinc-400 text-sm mt-1">#{entity.number}</p>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4">
            {entity.tuner && (
              <div className="bg-zinc-900 border border-zinc-800 p-3">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Team / Tuner</p>
                <p className="font-bold text-white">{entity.tuner}</p>
              </div>
            )}
            {entity.nationality && (
              <div className="bg-zinc-900 border border-zinc-800 p-3">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Nationality</p>
                <p className="font-bold text-white">{entity.nationality}</p>
              </div>
            )}
            <div className="bg-zinc-900 border border-zinc-800 p-3">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Price</p>
              <p className={`font-mono font-bold ${config.color}`}>
                {entity.price?.toLocaleString()} credits
              </p>
            </div>
          </div>

          {/* Description */}
          {entity.description && (
            <div>
              <h3 className="font-heading font-bold text-lg text-white mb-2 flex items-center">
                <Star size={16} className={`mr-2 ${config.color}`} />
                About
              </h3>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
                {entity.description}
              </p>
            </div>
          )}

          {/* Achievements (for drivers/riders) */}
          {entity.achievements && (
            <div>
              <h3 className="font-heading font-bold text-lg text-white mb-2 flex items-center">
                <Trophy size={16} className="mr-2 text-yellow-500" />
                Achievements
              </h3>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
                {entity.achievements}
              </p>
            </div>
          )}

          {/* Stats (for drivers/riders) */}
          {entity.stats && (
            <div>
              <h3 className="font-heading font-bold text-lg text-white mb-2 flex items-center">
                <Gauge size={16} className="mr-2 text-blue-500" />
                Stats
              </h3>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
                {entity.stats}
              </p>
            </div>
          )}

          {/* Specs (for cars/bikes) */}
          {entity.specs && (
            <div>
              <h3 className="font-heading font-bold text-lg text-white mb-2 flex items-center">
                <Gauge size={16} className="mr-2 text-blue-500" />
                Specifications
              </h3>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
                {entity.specs}
              </p>
            </div>
          )}

          {/* History (for tuners) */}
          {entity.history && (
            <div>
              <h3 className="font-heading font-bold text-lg text-white mb-2 flex items-center">
                <Calendar size={16} className="mr-2 text-purple-500" />
                History
              </h3>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
                {entity.history}
              </p>
            </div>
          )}

          {/* No description placeholder */}
          {!entity.description && !entity.achievements && !entity.stats && !entity.specs && !entity.history && (
            <div className="text-center py-8">
              <p className="text-zinc-500 italic">No additional information available yet.</p>
            </div>
          )}

          {/* Action Buttons */}
          {onSelect && (
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                data-testid="entity-modal-back"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-bold transition-colors"
              >
                <ArrowLeft size={18} /> Go Back
              </button>
              <button
                onClick={() => { onSelect(); onClose(); }}
                data-testid="entity-modal-select"
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-bold transition-colors ${
                  isSelected
                    ? 'bg-red-600 hover:bg-red-500 text-white'
                    : `${config.bgColor} border-2 ${config.borderColor} ${config.color} hover:opacity-80`
                }`}
              >
                <Check size={18} /> {isSelected ? 'Deselect' : 'Select'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
